import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { Avatar, cn, StatusBadge, Table, TableBody, TableCell, TableHeader, TableRow } from '@immo/ui-kit';
import { ColumnsTrigger, type ColumnPreference } from '@immo/components';
import { WHATSAPP_OPERATOR_LIST_MAX_LIMIT, type MessengerChatListItem } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { memberInitials } from '@/lib/member-initials';
import { useTranslations } from '@/i18n/use-translations';
import { ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { ChatIcon } from '@/components/layout/icons';
import { OperatorColumnHead } from '@/components/table/operator-column-head';
import type { OperatorColumn } from '@/components/table/operator-column';
import { loadOperatorColumnPreference, OPERATOR_COLUMN_KEYS } from '@/components/table/operator-column-store';
import { useOperatorTable } from '@/components/table/use-operator-table';

const DASH = '—';

type MessengerChatsLoaderResult = {
  readonly data: ReadonlyArray<MessengerChatListItem>;
  readonly truncated: boolean;
  readonly columnPreference: ColumnPreference;
};

export const messengerChatsLoaderParams = [] as const;

export async function messengerChatsLoader(): Promise<MessengerChatsLoaderResult> {
  const [result, columnPreference] = await Promise.all([
    apiClient.adminMessengerChats.list({ query: { limit: WHATSAPP_OPERATOR_LIST_MAX_LIMIT } }),
    loadOperatorColumnPreference(OPERATOR_COLUMN_KEYS.messengerChats),
  ]);

  if (!result.ok) {
    throw new Response('Failed to load messenger chats', { status: result.status });
  }

  return { data: result.body.data, truncated: result.body.truncated, columnPreference };
}

function useMessengerChatColumns(): ReadonlyArray<OperatorColumn<MessengerChatListItem>> {
  const t = useTranslations('pages.admin.messenger-chats');
  const intl = useIntl();

  return useMemo(
    () => [
      {
        id: 'channel',
        label: t('columns.channel'),
        cell: (row) => t(`channels.${row.messenger}`),
        sortValue: (row) => t(`channels.${row.messenger}`),
      },
      {
        id: 'account',
        label: t('columns.account'),
        cellClassName: 'whitespace-nowrap',
        cell: (row) => row.account,
        sortValue: (row) => row.account,
      },
      {
        id: 'member',
        label: t('columns.member'),
        cell: (row) =>
          row.memberName === null ? (
            <span className="text-text-placeholder">{DASH}</span>
          ) : (
            <div className="flex min-w-0 items-center gap-2">
              <Avatar initials={memberInitials(row.memberName)} seed={row.memberId} size="sm" />
              <span className="truncate">{row.memberName}</span>
            </div>
          ),
        sortValue: (row) => row.memberName,
      },
      {
        id: 'workspace',
        label: t('columns.workspace'),
        cell: (row) => row.workspaceName ?? <span className="text-text-placeholder">{DASH}</span>,
        sortValue: (row) => row.workspaceName,
      },
      {
        id: 'last-message',
        label: t('columns.last-message'),
        cellClassName: 'whitespace-nowrap',
        cell: (row) =>
          row.lastMessageAt === null ? (
            <span className="text-text-placeholder">{DASH}</span>
          ) : (
            intl.formatDate(row.lastMessageAt, { dateStyle: 'medium', timeStyle: 'short' })
          ),
        sortValue: (row) => (row.lastMessageAt === null ? null : Date.parse(row.lastMessageAt)),
      },
    ],
    [t, intl],
  );
}

export function MessengerChatsPage() {
  const t = useTranslations('pages.admin.messenger-chats');
  const navigate = useNavigate();
  const { data, truncated, columnPreference } = useLoaderData() as MessengerChatsLoaderResult;

  const catalogue = useMessengerChatColumns();
  const table = useOperatorTable({
    rows: data,
    preferenceKey: OPERATOR_COLUMN_KEYS.messengerChats,
    catalogue,
    alwaysVisibleId: 'account',
    loadedPreference: columnPreference,
  });

  return (
    <>
      <PageTopbar
        leading={<ListLeading title={t('title')} count={data.length} icon={ChatIcon} />}
        trailing={<StatusBadge tone="warning" size="sm" label={t('experimental')} />}
      />
      <div className="space-y-4">
        <p className="text-text-tertiary">{t('description')}</p>
        {truncated && (
          <p className="text-sm text-text-tertiary">{t('truncated', { limit: WHATSAPP_OPERATOR_LIST_MAX_LIMIT })}</p>
        )}
        {data.length === 0 ? (
          <p className="text-text-tertiary">{t('empty')}</p>
        ) : (
          <>
            <div className="flex justify-end">
              <ColumnsTrigger {...table.columns.trigger} />
            </div>
            <Table>
              <TableHeader>
                <TableRow {...table.columns.headerRowProps}>
                  {table.columns.visibleColumns.map((column) => (
                    <OperatorColumnHead key={column.id} column={column} table={table} />
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {table.sorted.map((row) => (
                  <TableRow
                    key={row.chatId}
                    interactive
                    tabIndex={0}
                    className="focus-visible:outline-none focus-visible:bg-hover-bg focus-visible:shadow-focus-row"
                    onClick={() => navigate(`/messenger/chats/${row.chatId}`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && event.target === event.currentTarget) {
                        navigate(`/messenger/chats/${row.chatId}`);
                      }
                    }}
                  >
                    {table.columns.visibleColumns.map((column) => (
                      <TableCell
                        key={column.id}
                        className={cn(column.cellClassName, table.columns.bodyCellClassName(column.id))}
                      >
                        {column.cell(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </div>
    </>
  );
}
